import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { CrossIcon } from '@net0-calc/common/assets/svg';
import { MapPoint } from 'common/types';
import React, { FC, useRef, useState } from 'react';
import { FieldRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'services';
import styled from 'styled-components/macro';
import usePlacesAutocomplete, { getGeocode, getLatLng, Suggestion } from 'use-places-autocomplete';

export type SearchAddressInputValue = { mainAddress: string; fullAddress: string; mapPoint: MapPoint };

interface SearchAddressInputProps extends FieldRenderProps<SearchAddressInputValue> {
  className?: string;
  isDisabled?: boolean;
  label?: string;
  placeholder?: string;
}

const COMMON_LOCATIONS = [
  {
    mainText: 'Paris',
    secondaryText: 'France',
    emoji: '🇫🇷',
    lat: 48.8566,
    lng: 2.3522,
    fullAddress: 'Paris, France',
  },
  {
    mainText: 'London',
    secondaryText: 'United Kingdom',
    emoji: '🇬🇧',
    lat: 51.5074,
    lng: -0.1278,
    fullAddress: 'London, UK',
  },
  {
    mainText: 'New York',
    secondaryText: 'United States',
    emoji: '🇺🇸',
    lat: 40.7128,
    lng: -74.006,
    fullAddress: 'New York, NY, USA',
  },
  {
    mainText: 'Milan',
    secondaryText: 'Italy',
    emoji: '🇮🇹',
    lat: 45.4642,
    lng: 9.19,
    fullAddress: 'Milan, Italy',
  },
  {
    mainText: 'Rome',
    secondaryText: 'Italy',
    emoji: '🇮🇹',
    lat: 41.9028,
    lng: 12.4964,
    fullAddress: 'Rome, Italy',
  },
  {
    mainText: 'Nice',
    secondaryText: 'France',
    emoji: '🇫🇷',
    lat: 43.7102,
    lng: 7.262,
    fullAddress: 'Nice, France',
  },
  {
    mainText: 'Geneva',
    secondaryText: 'Switzerland',
    emoji: '🇨🇭',
    lat: 46.2044,
    lng: 6.1432,
    fullAddress: 'Geneva, Switzerland',
  },
  {
    mainText: 'Munich',
    secondaryText: 'Germany',
    emoji: '🇩🇪',
    lat: 48.1351,
    lng: 11.582,
    fullAddress: 'Munich, Germany',
  },
  {
    mainText: 'Dubai',
    secondaryText: 'United Arab Emirates',
    emoji: '🇦🇪',
    lat: 25.2048,
    lng: 55.2708,
    fullAddress: 'Dubai, UAE',
  },
  {
    mainText: 'Los Angeles',
    secondaryText: 'United States',
    emoji: '🇺🇸',
    lat: 34.0522,
    lng: -118.2437,
    fullAddress: 'Los Angeles, CA, USA',
  },
];

export const SearchAddressInput: FC<SearchAddressInputProps> = ({ className, label, placeholder, input, meta }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const placesAutocompleter = usePlacesAutocomplete({
    defaultValue: input.value.fullAddress,
    requestOptions: {
      /* Define search scope here */
    },
    debounce: 300,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetValue = event.currentTarget.value;
    placesAutocompleter.setValue(targetValue);
  };

  const handleInputRootClick = () => {
    inputRef.current && inputRef.current.focus();
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsFocused(true);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setIsFocused(false), 150);
  };

  const onSelectCommonLocation = (loc: (typeof COMMON_LOCATIONS)[number]) => () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    inputRef.current?.blur();
    setIsFocused(false);
    placesAutocompleter.setValue(loc.fullAddress, false);
    placesAutocompleter.clearSuggestions();
    input.onChange({
      ...input.value,
      mainAddress: loc.mainText,
      fullAddress: loc.fullAddress,
      mapPoint: { lat: loc.lat, lng: loc.lng },
    });
  };

  const onSelectItem = (suggestion: Suggestion) => async () => {
    inputRef.current && inputRef.current.blur();

    const value = suggestion.description;

    placesAutocompleter.setValue(suggestion.description, false);
    placesAutocompleter.clearSuggestions();

    try {
      const [addressGeocode] = await getGeocode({ address: value });
      const mapPoint = await getLatLng(addressGeocode);

      input.onChange({
        ...input.value,
        mainAddress: suggestion.structured_formatting.main_text,
        fullAddress: addressGeocode.formatted_address,
        mapPoint: mapPoint,
      });
    } catch (error) {
      toast.error((error as Error)?.message);
    }
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    placesAutocompleter.setValue('');
    input.onChange({ adderss: undefined, mapPoint: undefined });
  };

  const hasError = meta?.touched && meta?.error;
  const { suggestions } = placesAutocompleter;
  return (
    <Root className={className}>
      <StyledLabel>{label}</StyledLabel>
      <InputWrap $hasError={hasError} onClick={handleInputRootClick}>
        <StyledInput
          ref={inputRef}
          value={placesAutocompleter.value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={!placesAutocompleter.ready}
          placeholder={placeholder}
          autoComplete="off"
        />
        {placesAutocompleter.value && (
          <ClearButton tabIndex={-1} type="button" onClick={handleClear}>
            <CrossIcon />
          </ClearButton>
        )}
        {isFocused && !placesAutocompleter.value && placesAutocompleter.suggestions.status !== 'OK' && (
          <SearchResultList>
            <CommonLocationsHeader>{t('common.search.mostCommon')}</CommonLocationsHeader>
            {COMMON_LOCATIONS.map((loc, index) => (
              <SearchResultListItem key={`${loc.mainText}-${index}`} onClick={onSelectCommonLocation(loc)}>
                <PlaceEmoji>{loc.emoji}</PlaceEmoji>
                <PlaceDetails>
                  <PlaceMainText>{loc.mainText}</PlaceMainText>
                  <PlaceSecondaryText>{loc.secondaryText}</PlaceSecondaryText>
                </PlaceDetails>
              </SearchResultListItem>
            ))}
          </SearchResultList>
        )}
        {placesAutocompleter.suggestions.status === 'OK' && (
          <SearchResultList>
            {suggestions.data.length === 0 && (
              <SearchEmptyResultItem>{t('common.search.noResults')}</SearchEmptyResultItem>
            )}
            {suggestions.data.map(suggestion => {
              const {
                place_id,
                structured_formatting: { main_text, secondary_text },
                types,
              } = suggestion;

              // Choose emoji based on place type
              const getPlaceEmoji = () => {
                if (types?.includes('airport')) return '✈️';
                if (types?.includes('country')) return '🌍';
                if (types?.includes('locality') || types?.includes('city')) return '🏙️';
                if (types?.includes('administrative_area_level_1')) return '📍';
                if (types?.includes('establishment')) return '🏢';
                if (types?.includes('street_address') || types?.includes('route')) return '🛣️';
                return '📍';
              };

              return (
                <SearchResultListItem key={place_id} onClick={onSelectItem(suggestion)}>
                  <PlaceEmoji>{getPlaceEmoji()}</PlaceEmoji>
                  <PlaceDetails>
                    <PlaceMainText>{main_text}</PlaceMainText>
                    {secondary_text && <PlaceSecondaryText>{secondary_text}</PlaceSecondaryText>}
                  </PlaceDetails>
                </SearchResultListItem>
              );
            })}
          </SearchResultList>
        )}
      </InputWrap>
      {hasError && <ErrorText>{meta.error}</ErrorText>}
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
`;

const StyledInput = styled.input`
  ${TYPOGRAPHY.bodyRegular16}
  width: 80%;
  color: var(--color6);
  outline: none;
`;

const InputWrap = styled.div<{ $hasError?: boolean }>`
  position: relative;
  width: 100%;
  height: 64px;
  padding: 18px;

  border-radius: 15px;
  border: 2px solid ${({ $hasError }) => ($hasError ? 'var(--color12)' : 'var(--color10)')};

  background-color: var(--color5);

  cursor: text;
  transition: all 0.2s;
  margin-bottom: 24px;

  @media ${DEVICE.laptop} {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${({ $hasError }) => ($hasError ? 'var(--color12)' : 'var(--color9)')};
  }

  &:focus-within {
    border: 2px solid var(--color2);
  }
`;

const SearchResultList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 500;
  border-radius: 8px;
  box-shadow: 0px 5px 10px var(--color10);

  width: 100%;
  max-height: 230px;
  background-color: var(--color5);

  margin: 0;
  padding: 0;
  overflow: auto;
  list-style: none;
`;

const CommonLocationsHeader = styled.li`
  ${TYPOGRAPHY.bodyRegular14};
  color: var(--color9);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 12px 16px 8px;
  margin: 0;
  border-bottom: 1px solid var(--color10);
`;

const SearchResultListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--color6);

  width: 100%;
  padding: 14px 16px;

  cursor: pointer;

  &:not(:last-child) {
    border-bottom: 1px solid var(--color10);
  }

  &:hover {
    background-color: var(--color8);
  }
`;

const PlaceEmoji = styled.span`
  font-size: 24px;
  flex-shrink: 0;
  width: 32px;
  text-align: center;
`;

const PlaceDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const PlaceMainText = styled.span`
  ${TYPOGRAPHY.bodyMedium16};
  color: var(--color6);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlaceSecondaryText = styled.span`
  ${TYPOGRAPHY.bodyRegular14};
  color: var(--color9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SearchEmptyResultItem = styled.li`
  ${TYPOGRAPHY.bodyMedium16};
  color: var(--color9);
  width: 100%;
  padding: 16px;
  text-align: center;
`;

const StyledLabel = styled.label`
  ${TYPOGRAPHY.bodyRegular16};
  display: block;
  margin-bottom: 8px;
  color: var(--color9);
`;

const ErrorText = styled.span`
  ${TYPOGRAPHY.captionRegular12}
  display: block;
  margin-top: 4px;
  color: var(--color12);
`;

const ClearButton = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  cursor: pointer;
`;
